/**
 * A function to create a log object for the 'bmDeleteIconClick' event (evid: 4544)
 *
 * Event description: Sent when a user clicks Delete icon in BM Comments list or click Permanently delete button in Comment page in BM
 *
 * @param {bmDeleteIconClickParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} comment_id - comment guid
 * @property {GUID} msid - site id - msid
 * @property {string} origin - list / comment page
 * @property {GUID} post_stable_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} tab_name - published / pending / rejected
 * @return {LogObject<bmDeleteIconClickParams>} LogObject<bmDeleteIconClickParams>
 */
export function bmDeleteIconClick(params) {
    return { evid: 4544, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'guestCommentingChoice' event (evid: 4529)
 *
 * Event description: Event triggered when clicking on one of guest commenting options (Publish as Member / Publish as Guest)
 *
 * @param {guestCommentingChoiceParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {string} option_value - publish_as_member / publish_as_guest
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} post_stable_id
 * @property {GUID} site_member_id - site member id
 * @property {string} type - comment/reply
 * @property {GUID} visitor_id - visitor id
 * @return {LogObject<guestCommentingChoiceParams>} LogObject<guestCommentingChoiceParams>
 */
export function guestCommentingChoice(params) {
    return { evid: 4529, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'bmFiltersApplied' event (evid: 4546)
 *
 * Event description: Sent when a user changes filtering in BM
 *
 * @param {bmFiltersAppliedParams} params - The parameters for this event
 * @property {string} action_type - "add"/"remove"
 * @property {GUID} app_id - app_id
 * @property {string} current_filters - The current state of filters, following the filter trigger
 * @property {GUID} msid - site id - msid
 * @property {GUID} post_stable_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} tab_name - current tab name
 * @return {LogObject<bmFiltersAppliedParams>} LogObject<bmFiltersAppliedParams>
 */
export function bmFiltersApplied(params) {
    return { evid: 4546, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'paginationOpenDropDownList' event (evid: 4536)
 *
 * Event description: Event is triggered when user clicks on pagination drop down list to open it
 *
 * @param {paginationOpenDropDownListParams} params - The parameters for this event
 * @property {string} action_type - open / close
 * @property {GUID} app_id - app_id
 * @property {GUID} msid - msid
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} post_stable_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} visitor_id - visitor id
 * @return {LogObject<paginationOpenDropDownListParams>} LogObject<paginationOpenDropDownListParams>
 */
export function paginationOpenDropDownList(params) {
    return { evid: 4536, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'imageFinishUploading' event (evid: 4516)
 *
 * Event description: event triggered when a user finishes to upload Image (comes together with Image start uploading for success rate)
 *
 * @param {imageFinishUploadingParams} params - The parameters for this event
 * @property {GUID} comment_id - comment id
 * @property {GUID} biToken - msid
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} post_stable_id
 * @property {GUID} site_member_id - site member id
 * @property {string} type - comment / reply
 * @property {GUID} visitor_id - visitor
 * @return {LogObject<imageFinishUploadingParams>} LogObject<imageFinishUploadingParams>
 */
export function imageFinishUploading(params) {
    return { evid: 4516, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'paginationSelectPageFromTheList' event (evid: 4537)
 *
 * Event description: Event is triggered when user clicks on an option in drop down list
 *
 * @param {paginationSelectPageFromTheListParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} msid - msid
 * @property {string} page_name - selected page
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} post_stable_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} visitor_id - visitor id
 * @return {LogObject<paginationSelectPageFromTheListParams>} LogObject<paginationSelectPageFromTheListParams>
 */
export function paginationSelectPageFromTheList(params) {
    return { evid: 4537, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'fileDownloaded' event (evid: 4521)
 *
 * Event description: Event triggered when a person tries to download a file from comment or reply.
 *
 * @param {fileDownloadedParams} params - The parameters for this event
 * @property {string} file_ext - File extension
 * @property {string} file_id - The file identifier
 * @property {number} file_size - The size of the file being downloaded (in bytes)
 * @property {string} mime_type - File type
 * @property {GUID} biToken - metasite id
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} post_stable_id
 * @property {GUID} site_member_id - site_member_id
 * @property {boolean} success - 0 - no download link / 1 - download link received
 * @property {string} type - comment, reply, general
 * @property {GUID} uuid - uuid
 * @property {GUID} visitor_id - visitor id
 * @return {LogObject<fileDownloadedParams>} LogObject<fileDownloadedParams>
 */
export function fileDownloaded(params) {
    return { evid: 4521, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'bmItemSelectionToggled' event (evid: 4542)
 *
 * Event description: Sent when a user select/deselects an item(s) in a list in BM
 *
 * @param {bmItemSelectionToggledParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} click_type - The click purpose : "select" / "deselect"
 * @property {boolean} is_select_all - Did the user click the "Select All" check box (will get "true" regardless the label attached to the check box: "Select All" or "Deselect All").
 * @property {GUID} msid - site id - msid
 * @property {GUID} post_stable_id
 * @property {number} selected_count - number of reviews selected (after action)
 * @property {GUID} site_member_id - site_member_id
 * @property {string} tab_name - name of current tab
 * @return {LogObject<bmItemSelectionToggledParams>} LogObject<bmItemSelectionToggledParams>
 */
export function bmItemSelectionToggled(params) {
    return { evid: 4542, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'bmPublishComment' event (evid: 4545)
 *
 * Event description: Sent when a user clicks PUBLISH button in BM
 *
 * @param {bmPublishCommentParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} comment_id - comment guid
 * @property {number} comments_count - when all are selected
 * @property {boolean} is_select_all - Are all comments are selected?
 * @property {GUID} msid - site id - msid
 * @property {string} origin - list / comment page
 * @property {GUID} post_stable_id
 * @property {GUID} site_member_id - site_member_id
 * @return {LogObject<bmPublishCommentParams>} LogObject<bmPublishCommentParams>
 */
export function bmPublishComment(params) {
    return { evid: 4545, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'bmClearFilters' event (evid: 4547)
 *
 * Event description: Sent when a user clears filters in BM
 *
 * @param {bmClearFiltersParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} msid - site id - msid
 * @property {string} origin - comments_list / filter_panel
 * @property {GUID} post_stable_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} tab_name - current tab name
 * @return {LogObject<bmClearFiltersParams>} LogObject<bmClearFiltersParams>
 */
export function bmClearFilters(params) {
    return { evid: 4547, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'mentionSuggestionSelected' event (evid: 4519)
 *
 * Event description: When a user clicks one user from the mentioned list
 *
 * @param {mentionSuggestionSelectedParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} post_stable_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} type - comment/ reply
 * @property {GUID} uuid - uuid
 * @property {GUID} visitor_id - visitor id
 * @return {LogObject<mentionSuggestionSelectedParams>} LogObject<mentionSuggestionSelectedParams>
 */
export function mentionSuggestionSelected(params) {
    return { evid: 4519, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'commentsListLoaded' event (evid: 4501)
 *
 * Event description: Event triggered when comments list is fully loaded for the first time. Not  sent during pagination or after 'load more' button pressed.
 *
 * @param {commentsListLoadedParams} params - The parameters for this event
 * @property {number} comments_count - how many comments loaded
 * @property {GUID} biToken - site id - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} parent_resource_id - parent_resource_id
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @return {LogObject<commentsListLoadedParams>} LogObject<commentsListLoadedParams>
 */
export function commentsListLoaded(params) {
    return { evid: 4501, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'attemptToPublishCommentReply' event (evid: 4502)
 *
 * Event description: Triggered when a user/member/visitor attempts to publish (clicks "publish") a comment/reply
 *
 * @param {attemptToPublishCommentReplyParams} params - The parameters for this event
 * @property {string} publish_type - - publish_as_member (when user selects to publish comment as member)
 *  - publish_as_guest (when user selects to publish comment as guest)
 *  - sign_in (for cases when we don’t give option to comment as guest and show sign_in window)
 * - logged_in_member (for cases when user is already logged in)
 * @property {GUID} biToken - msid
 * @property {number} symbol_count - the amount on text symbols in the comment/reply
 * @property {GUID} visitor_id - visitor id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - comments_page, search, user_profile, business_manager
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site member id
 * @property {string} type - comment, reply
 * @property {string} attempt_type - new/edit. `edit` value fires when user edits comment/reply
 * @return {LogObject<attemptToPublishCommentReplyParams>} LogObject<attemptToPublishCommentReplyParams>
 */
export function attemptToPublishCommentReply(params) {
    return { evid: 4502, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'commentReplyPublished' event (evid: 4503)
 *
 * Event description: Event triggered after comment/reply is successfully published
 *
 * @param {commentReplyPublishedParams} params - The parameters for this event
 * @property {number} image_count - the number of images in the post
 * @property {boolean} has_video - 0 - no video/ 1 - with video
 * @property {boolean} has_mention - 0 - no mention / 1 - with mention
 * @property {boolean} is_edited - 0 - new post / 1 - edit (to know if its published for first time)
 * @property {string} parent_comment_id - for comments = Null. For reply - parent comment/reply ID
 * @property {number} request_duration - Length of time between attempt and published
 * @property {GUID} biToken - msid
 * @property {string} media_source - "file"(when uploading file in web) / "gallery"(when uploading file from oneApp ) / "camera" (for oneApp)  / "link" (adding video using link in Web), "mixed" - if added from different sources
 * @property {boolean} has_gallery - 0 - no gallery, 1 - with gallery
 * @property {number} hashtag_count - the number of hashtags in post
 * @property {GUID} visitor_id - visitor id
 * @property {string} comment_id - comment/reply id
 * @property {number} mention_count - the number of mentions in post
 * @property {boolean} has_gif - 0 - no gif / 1 - has gif
 * @property {GUID} post_stable_id - post_stable_id
 * @property {boolean} has_image - 0 - no image / 1 - with image
 * @property {boolean} is_custom_video - 0 - no custom video / 1 - has video uploaded through video upload
 * @property {number} rating - 1, 2, 3, 4 ,5 - in Blog app. Null if not applicable
 * @property {number} image_in_gallery_count - the number of images in gallery
 * @property {string} origin - comments_page, search, user_profile, business_manager
 * @property {boolean} has_quote - 0 - no quote / 1 - with quote
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {boolean} published_as_guest - 0 - published as member / 1 - published as guest
 * @property {GUID} app_id - app_id
 * @property {number} file_count - Number of files attached to a comment
 * @property {GUID} site_member_id - site_member_id
 * @property {string} type - comment,  reply
 * @property {boolean} has_hashtag - 0 - no hashtag / 1 - has hashtag
 * @property {boolean} has_emoji - 0 - no emoji / 1 - with emoji
 * @return {LogObject<commentReplyPublishedParams>} LogObject<commentReplyPublishedParams>
 */
export function commentReplyPublished(params) {
    return { evid: 4503, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'likeClicked' event (evid: 4504)
 *
 * Event description: Event triggered after like/reaction button is clicked
 *
 * @param {likeClickedParams} params - The parameters for this event
 * @property {GUID} biToken - site msid
 * @property {GUID} visitor_id - visitor id
 * @property {string} reaction_type - ‘wow’,‘haha’, ’sad, ‘like’, ‘angry’
 * @property {string} comment_id - comment/reply id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - whether from Discover or not
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {boolean} flag - 0 - unliked / 1 - like
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - the member_id after login to Wix
 * @property {string} type - comment, reply
 * @property {string} action - add (used for new ones reaction added), replace, remove
 * @return {LogObject<likeClickedParams>} LogObject<likeClickedParams>
 */
export function likeClicked(params) {
    return { evid: 4504, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'likePublished' event (evid: 4505)
 *
 * Event description: Event triggered after registering like
 *
 * @param {likePublishedParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {string} reaction_type - ‘wow’,‘haha’, ’sad, ‘like’, ‘angry’
 * @property {string} comment_id - comment/reply id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - comments_page, search_results, user_profile
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {boolean} flag - False - unliked / true - like
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} type - comment, reply
 * @property {string} action - add (used for new ones reaction added), replace, remove
 * @return {LogObject<likePublishedParams>} LogObject<likePublishedParams>
 */
export function likePublished(params) {
    return { evid: 4505, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'loadMoreClick' event (evid: 4506)
 *
 * Event description: Event triggered when user clicks on "View more comments" or "View older replies"
 *
 * @param {loadMoreClickParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - comments_page, search
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site member id
 * @property {string} type - comment/reply
 * @property {string} action - previous / next
 * @return {LogObject<loadMoreClickParams>} LogObject<loadMoreClickParams>
 */
export function loadMoreClick(params) {
    return { evid: 4506, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'commentsSort' event (evid: 4507)
 *
 * Event description: Event triggered when user changes sorting of comments
 *
 * @param {commentsSortParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - comments_page / search / business_manager
 * @property {string} sort_by - oldest / newest
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app guid
 * @property {GUID} site_member_id - site_member_id
 * @return {LogObject<commentsSortParams>} LogObject<commentsSortParams>
 */
export function commentsSort(params) {
    return { evid: 4507, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'dotsActionButtonOpenActions' event (evid: 4508)
 *
 * Event description: Event triggered after user clicks on 3 dots action button
 *
 * @param {dotsActionButtonOpenActionsParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {string} comment_id - comment/reply id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - comments_page, search, user_profile, business_manager
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} type - comment/reply
 * @return {LogObject<dotsActionButtonOpenActionsParams>} LogObject<dotsActionButtonOpenActionsParams>
 */
export function dotsActionButtonOpenActions(params) {
    return { evid: 4508, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'dotsActionButtonActionClick' event (evid: 4509)
 *
 * Event description: Event triggered when clicking on one of the actions
 *
 * @param {dotsActionButtonActionClickParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {string} comment_id - comment/reply id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - comments_page, search_results, user_profile, business_manager
 * @property {string} parent_resource_id - parent_resource_id
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} type - comment / reply
 * @property {string} action - edit, delete (eg. move_to_trash, list_comment_delete_comment), share, report, copy_link, mark, copy_text, delete_member, view_live (eg. list_comment_view_live), view_member ((eg. list_comment_view_member), move_comment
 * @return {LogObject<dotsActionButtonActionClickParams>} LogObject<dotsActionButtonActionClickParams>
 */
export function dotsActionButtonActionClick(params) {
    return { evid: 4509, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'cancelWritingEditingComment' event (evid: 4510)
 *
 * Event description: Event triggered after you cancel comment writing/editing
 *
 * @param {cancelWritingEditingCommentParams} params - The parameters for this event
 * @property {GUID} biToken - site msid
 * @property {GUID} visitor_id - visitor id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app id
 * @property {GUID} site_member_id - site member id
 * @property {string} type - comment / reply
 * @return {LogObject<cancelWritingEditingCommentParams>} LogObject<cancelWritingEditingCommentParams>
 */
export function cancelWritingEditingComment(params) {
    return { evid: 4510, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'emptyStateShown' event (evid: 4511)
 *
 * Event description: Event triggered when empty state is shown ( app loaded but no comments )
 *
 * @param {emptyStateShownParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} uuid - uuid
 * @property {GUID} visitor_id - visitor id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} site_member_id - site member id
 * @return {LogObject<emptyStateShownParams>} LogObject<emptyStateShownParams>
 */
export function emptyStateShown(params) {
    return { evid: 4511, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'customErrorPageRendered' event (evid: 4512)
 *
 * Event description: When we render a custom error page when an unhandled exception is thrown by a request
 *
 * @param {customErrorPageRenderedParams} params - The parameters for this event
 * @property {GUID} biToken - site guid
 * @property {GUID} visitor_id - visitor id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} site_member_id - the member_id after login to Wix
 * @return {LogObject<customErrorPageRenderedParams>} LogObject<customErrorPageRenderedParams>
 */
export function customErrorPageRendered(params) {
    return { evid: 4512, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'goToMemberProfile' event (evid: 4513)
 *
 * Event description: Event triggered when clicking on avatar to go to user profile
 *
 * @param {goToMemberProfileParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {boolean} is_my_profile - 1 - is own profile , 0 - not
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - post_page, search_results
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} site_member_id - site member id
 * @return {LogObject<goToMemberProfileParams>} LogObject<goToMemberProfileParams>
 */
export function goToMemberProfile(params) {
    return { evid: 4513, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'replyingOnClicked' event (evid: 4514)
 *
 * Event description: Event triggered after "Replying on" button is clicked
 *
 * @param {replyingOnClickedParams} params - The parameters for this event
 * @property {GUID} biToken - site msid
 * @property {GUID} uuid - uuid
 * @property {GUID} visitor_id - site visitor id
 * @property {GUID} comment_id - comment id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} site_member_id - the member_id after login to Wix forum
 * @property {string} type - comment / reply
 * @return {LogObject<replyingOnClickedParams>} LogObject<replyingOnClickedParams>
 */
export function replyingOnClicked(params) {
    return { evid: 4514, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'imageStartUploading' event (evid: 4515)
 *
 * Event description: event triggered when a user starts to upload Image (comes together with Image finish uploading for success rate)
 *
 * @param {imageStartUploadingParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {GUID} comment_id - comment id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - image type - cover, avatar, post editor
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} site_member_id - site member id
 * @property {string} type - comment / reply
 * @return {LogObject<imageStartUploadingParams>} LogObject<imageStartUploadingParams>
 */
export function imageStartUploading(params) {
    return { evid: 4515, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'onShowErrorToast' event (evid: 4517)
 *
 * Event description: When UoU tries to publish comment/reply and error occurs.
 *
 * @param {onShowErrorToastParams} params - The parameters for this event
 * @property {GUID} biToken - site guid
 * @property {GUID} visitor_id - visitor id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {string} error_message - error message/type
 * @property {GUID} site_member_id - the member_id after login to Wix
 * @property {string} type - comment/reply
 * @property {string} attempt_type - new/edit. "edit" value fires when user edits comment/reply
 * @return {LogObject<onShowErrorToastParams>} LogObject<onShowErrorToastParams>
 */
export function onShowErrorToast(params) {
    return { evid: 4517, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'mentionClickedFromPublishedComment' event (evid: 4518)
 *
 * Event description: When a user clicks on a mention from published comment/reply
 *
 * @param {mentionClickedFromPublishedCommentParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} uuid - uuid
 * @property {GUID} visitor_id - visitor id
 * @property {GUID} comment_id - comment id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} site_member_id - site_member_id
 * @property {string} type - comment/reply
 * @return {LogObject<mentionClickedFromPublishedCommentParams>} LogObject<mentionClickedFromPublishedCommentParams>
 */
export function mentionClickedFromPublishedComment(params) {
    return { evid: 4518, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'fileUploaded' event (evid: 4520)
 *
 * Event description: Event triggered when a person tries to upload a file to a comment or reply
 *
 * @param {fileUploadedParams} params - The parameters for this event
 * @property {string} file_ext - File extension
 * @property {boolean} success - 0 - upload failed / 1 - upload successful
 * @property {string} file_id - The file identifier (null if not successful)
 * @property {GUID} biToken - metasite id
 * @property {number} file_size - The size of the file being uploaded (in bytes)
 * @property {GUID} uuid - uuid
 * @property {GUID} visitor_id - visitor id
 * @property {GUID} comment_id - comment id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} mime_type - File type
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {string} type - comment, reply
 * @return {LogObject<fileUploadedParams>} LogObject<fileUploadedParams>
 */
export function fileUploaded(params) {
    return { evid: 4520, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'logInSignUpToComment' event (evid: 4522)
 *
 * Event description: Event triggered when clicking on sign up or login in comment page
 *
 * @param {logInSignUpToCommentParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} site_member_id - site member id
 * @property {string} type - login/signup/unknown
 * @return {LogObject<logInSignUpToCommentParams>} LogObject<logInSignUpToCommentParams>
 */
export function logInSignUpToComment(params) {
    return { evid: 4522, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'whoLikedReactedClick' event (evid: 4523)
 *
 * Event description: Event triggered when UoU clicks on list of likes/reactions
 *
 * @param {whoLikedReactedClickParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {string} reaction_type - "likes" or "reactions"
 * @property {string} comment_id - comment/reply id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - comments_page, search_results, user_profile
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} type - "comment" or "reply"
 * @return {LogObject<whoLikedReactedClickParams>} LogObject<whoLikedReactedClickParams>
 */
export function whoLikedReactedClick(params) {
    return { evid: 4523, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'followMember' event (evid: 4524)
 *
 * Event description: Event triggered when clicking on follow user button
 *
 * @param {followMemberParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {string} member_followed - the member_id that had been followed
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - search_results, user_profile, who_liked_my_comment
 * @property {boolean} flag - 0 - unfollow / 1 - follow
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - the member_id after login to Wix forum
 * @return {LogObject<followMemberParams>} LogObject<followMemberParams>
 */
export function followMember(params) {
    return { evid: 4524, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'listOfReactionsClickOnTab' event (evid: 4525)
 *
 * Event description: click on tab in list of reactions
 *
 * @param {listOfReactionsClickOnTabParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {string} tab_name - tab name : 'all', ‘wow’,‘haha’, ’sad, ‘like’, ‘angry’
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comment_id - comment_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @return {LogObject<listOfReactionsClickOnTabParams>} LogObject<listOfReactionsClickOnTabParams>
 */
export function listOfReactionsClickOnTab(params) {
    return { evid: 4525, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'commentsUpvoteDownvoteClick' event (evid: 4526)
 *
 * Event description: Event is fired when UoU clicks on upvote/downvote buttons on comments
 *
 * @param {commentsUpvoteDownvoteClickParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {boolean} is_best_answer - True If comment is marked as best answer
 * @property {string} comment_id - comment GUID ID
 * @property {number} final_state - e.g. 1, 4, 5, 0, -1, -4, -5 etc.
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - comments_list / members_comments
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - the member_id after login to Wix forum
 * @property {string} type - comment/reply
 * @property {string} action_type - vote / unvote /downvote / undownvote
 * @return {LogObject<commentsUpvoteDownvoteClickParams>} LogObject<commentsUpvoteDownvoteClickParams>
 */
export function commentsUpvoteDownvoteClick(params) {
    return { evid: 4526, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'readMoreClicked' event (evid: 4527)
 *
 * Event description: Event triggered when user clicks on "Read more" or "Read less" (when text is truncated)
 *
 * @param {readMoreClickedParams} params - The parameters for this event
 * @property {string} parent_comment_id - For comments = Null. For reply - parent comment/reply ID
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - comments_page / user_profile / business_manager
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site member id
 * @property {string} type - comment/reply
 * @property {string} action - more / less
 * @return {LogObject<readMoreClickedParams>} LogObject<readMoreClickedParams>
 */
export function readMoreClicked(params) {
    return { evid: 4527, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'viewNewCommentClicked' event (evid: 4528)
 *
 * Event description: Event triggered when user clicks on "View New Comment" ( Live updates feature)
 *
 * @param {viewNewCommentClickedParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - comments_page
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site member id
 * @property {string} type - comment/reply
 * @return {LogObject<viewNewCommentClickedParams>} LogObject<viewNewCommentClickedParams>
 */
export function viewNewCommentClicked(params) {
    return { evid: 4528, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'publishButtonClick' event (evid: 4530)
 *
 * Event description: Triggered when a user/member/visitor first time clicks "publish" a comment/reply button
 *
 * @param {publishButtonClickParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site member id
 * @property {string} type - comment, reply
 * @return {LogObject<publishButtonClickParams>} LogObject<publishButtonClickParams>
 */
export function publishButtonClick(params) {
    return { evid: 4530, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'captchaOnCommentReplyPublish' event (evid: 4531)
 *
 * Event description: Event is triggered when captcha is shown to UoU and when he solves captcha (guest commenting)
 *
 * @param {captchaOnCommentReplyPublishParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {boolean} captcha_success - for captcha _step = 2 : 1 - success / 0 - failure
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app ID
 * @property {GUID} site_member_id - site_member_id
 * @property {string} type - comment, reply
 * @property {number} captcha_step - 1 - shown to user / 2 - solved (successfully or not )
 * @return {LogObject<captchaOnCommentReplyPublishParams>} LogObject<captchaOnCommentReplyPublishParams>
 */
export function captchaOnCommentReplyPublish(params) {
    return { evid: 4531, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'markAsBestCommentClick' event (evid: 4532)
 *
 * Event description: Mark as best comment click.
 *
 * @param {markAsBestCommentClickParams} params - The parameters for this event
 * @property {GUID} biToken - site msid
 * @property {GUID} visitor_id - visitor id
 * @property {string} comment_id - comment/reply id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - comments_page
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {boolean} flag - 0 - unmark as best / 1 - mark as best
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - the member_id after login to Wix
 * @property {string} type - comment, reply
 * @return {LogObject<markAsBestCommentClickParams>} LogObject<markAsBestCommentClickParams>
 */
export function markAsBestCommentClick(params) {
    return { evid: 4532, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'deleteCommentReply' event (evid: 4534)
 *
 * Event description: Event triggered when user or uou deletes a Comment /Reply
 *
 * @param {deleteCommentReplyParams} params - The parameters for this event
 * @property {string} parent_comment_id - for comments = Null. For reply - parent comment/reply ID
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {string} comment_id - comment/reply id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} type - comment / reply
 * @property {string} app_instance_id - app_instance_id
 * @return {LogObject<deleteCommentReplyParams>} LogObject<deleteCommentReplyParams>
 */
export function deleteCommentReply(params) {
    return { evid: 4534, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'intentClickCommentReply' event (evid: 4535)
 *
 * Event description: Event is triggered when user / UoU click into comment container or reply button
 *
 * @param {intentClickCommentReplyParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - comments_page, search_results, user_profile, business_manager
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} type - comment,  reply
 * @return {LogObject<intentClickCommentReplyParams>} LogObject<intentClickCommentReplyParams>
 */
export function intentClickCommentReply(params) {
    return { evid: 4535, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'commentPageOpened' event (evid: 4538)
 *
 * Event description: Event triggered when comment opened in BM
 *
 * @param {commentPageOpenedParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comment_id - comment_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - business_manager
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} site_member_id - site_member_id
 * @property {string} type - open_from_list / open_with_arrow
 * @property {GUID} instance_id - instance_id
 * @return {LogObject<commentPageOpenedParams>} LogObject<commentPageOpenedParams>
 */
export function commentPageOpened(params) {
    return { evid: 4538, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'businessManagerFinishedLoadingCommentsTab' event (evid: 4539)
 *
 * Event description: Event triggered when BM finishes loading for a user Comments Tab. This tab shows Comments Widget Comments
 *
 * @param {businessManagerFinishedLoadingCommentsTabParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} appDefId - app def id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - "sidebar", "editor_gfpp", "editor_app_settings", "adi_manage", "adi_pages_panel", "dashboard widget", "manage tab"
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} instance_id - instance_id
 * @return {LogObject<businessManagerFinishedLoadingCommentsTabParams>} LogObject<businessManagerFinishedLoadingCommentsTabParams>
 */
export function businessManagerFinishedLoadingCommentsTab(params) {
    return { evid: 4539, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'viewLiveClick' event (evid: 4540)
 *
 * Event description: Event triggered after user clicks on View Live button in opened comment modal in BM
 *
 * @param {viewLiveClickParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {string} comment_id - comment_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - business_manager
 * @property {string} parent_resource_id - comments parent entity GUID (post, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @return {LogObject<viewLiveClickParams>} LogObject<viewLiveClickParams>
 */
export function viewLiveClick(params) {
    return { evid: 4540, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'bmClickOnTab' event (evid: 4541)
 *
 * Event description: Event triggered when user clicks on tab in BM
 *
 * @param {bmClickOnTabParams} params - The parameters for this event
 * @property {string} target_tab_name - name of target tab
 * @property {string} tab_name - name of current tab
 * @property {GUID} post_stable_id - post_stable_id
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmClickOnTabParams>} LogObject<bmClickOnTabParams>
 */
export function bmClickOnTab(params) {
    return { evid: 4541, src: 69, endpoint: 'comments-uou', params: params };
}
/**
 * A function to create a log object for the 'bmMoveComment' event (evid: 4543)
 *
 * Event description: Sent when a user clicks move to Trash button in BM
 *
 * @param {bmMoveCommentParams} params - The parameters for this event
 * @property {string} comment_id - comment_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @property {string} action - move / x / cancel
 * @return {LogObject<bmMoveCommentParams>} LogObject<bmMoveCommentParams>
 */
export function bmMoveComment(params) {
    return { evid: 4543, src: 69, endpoint: 'comments-uou', params: params };
}
//# sourceMappingURL=index.js.map